import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';

import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { ListaPrecoService } from '../../../../shared/services/apis/produto/lista-preco.service';
import { ReturnStruct } from '../../../../shared/services/structs/return.struct';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import {
  PageScrollSpyComponent,
  ScrollSpySection,
} from '../../../../shared/components/page-scroll-spy/page-scroll-spy.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MaskService } from '../../../../shared/services/mask.service';
import { LookupItemStruct } from '../../../../shared/services/structs/pessoa/lookup-item.struct';
import { ListaPrecoRequest } from '../../../../shared/services/requests/produto/lista-preco.request';
import { ProdutoListaPrecoStruct } from '../../../../shared/services/structs/produto/produto-lista-preco.struct';
import { ClienteListaPrecoStruct } from '../../../../shared/services/structs/produto/cliente-lista-preco.struct';
import { ListaPrecoCadastroProdutosComponent } from './lista-preco-cadastro-produtos/lista-preco-cadastro-produtos.component';
import { ListaPrecoCadastroClientesComponent } from './lista-preco-cadastro-clientes/lista-preco-cadastro-clientes.component';
import { AlertCardComponent } from '../../../../shared/components/alert-card/alert-card.component';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { UnidadeAjustePrecoEnum } from '../../../../shared/enums/produto/unidade-ajuste-preco.enum';
import { MetodoAjustePrecoEnum } from '@shared/enums/produto/metodo-ajuste-preco.enum';

@Component({
  selector: 'app-lista-preco-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    LoadingBoxComponent,
    PageScrollSpyComponent,
    FooterActionComponent,
    HeaderComponent,
    NgxCurrencyDirective,
    ListaPrecoCadastroProdutosComponent,
    ListaPrecoCadastroClientesComponent,
    AlertCardComponent,
  ],
  templateUrl: './lista-preco-cadastro.component.html',
  styleUrl: './lista-preco-cadastro.component.css',
})
export class ListaPrecoCadastroComponent implements OnInit {
  @ViewChild(ListaPrecoCadastroProdutosComponent) produtosComponent!: ListaPrecoCadastroProdutosComponent;

  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;

  public form: FormGroup;
  public isEditMode: boolean = false;
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  public isPadrao: boolean = false;
  public idListaPreco: number | null = null;

  public currencyMask = this.maskService.getCurrencyMask();
  public percentageMask = this.maskService.getDecimalMask2();
  public valorAjusteMask: any = { ...this.maskService.getPercentMask(), allowNegative: false };

  // Lookups
  public listaMetodosAjuste: LookupItemStruct[] = [];
  public listaAcoes: LookupItemStruct[] = [];
  public listaUnidades: LookupItemStruct[] = [];
  public listaTiposArredondamento: LookupItemStruct[] = [];

  // Listas dos sub-componentes
  public listaProdutos: ProdutoListaPrecoStruct[] = [];
  public listaClientes: ClienteListaPrecoStruct[] = [];

  // Scroll Spy
  public sections: ScrollSpySection[] = [
    { id: 'dados-gerais', label: 'Dados Gerais' },
    { id: 'itens-lista', label: 'Itens na Lista' },
    { id: 'clientes-lista', label: 'Clientes' },
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private listaPrecoService: ListaPrecoService,
    private alertService: AlertService,
    private maskService: MaskService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.carregarLookups();

    const paramId = this.route.snapshot.paramMap.get('id');
    if (paramId) {
      this.idListaPreco = Number(paramId);
      this.isEditMode = true;
      this.carregarDados(this.idListaPreco);
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      descricao: ['', Validators.required],
      isAtivo: [true],
      isPromocao: [false],

      idMetodoAjustePreco: [MetodoAjustePrecoEnum.Automatico, Validators.required],
      idAcaoAjustePreco: [null, Validators.required],
      idUnidadeAjustePreco: [UnidadeAjustePrecoEnum.Percentual, Validators.required],
      valorAjuste: [null, [Validators.required, Validators.min(0), Validators.max(100)]],

      isSelecaoProdutosAutomatica: [false, Validators.required],
      isTodosClientes: [false, Validators.required],

      idTipoArredondamento: [null],
      dataInicioVigencia: [null, Validators.required],
      dataTerminoVigencia: [null],

      percentualDescontoSugerido: [null],
      percentualDescontoMaximo: [null],
    });

    // Listeners
    this.form.get('isPromocao')?.valueChanges.subscribe(() => this.onPromocaoChange());
    this.form.get('idMetodoAjustePreco')?.valueChanges.subscribe(val => this.onMetodoAjusteChange(val));
    this.form.get('idUnidadeAjustePreco')?.valueChanges.subscribe(val => this.onUnidadeAjusteChange(val));
  }

  private carregarLookups(): void {
    this.listaPrecoService.getLookups().subscribe({
      next: res => {
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.listaMetodosAjuste = res.metodosAjustePreco;
        this.listaAcoes = res.acoesAjustePreco;
        this.listaUnidades = res.unidadesAjustePreco;
        this.listaTiposArredondamento = res.tiposArredondamento;

        // Após carregar lookups, aplicar defaults de criação
        if (!this.isEditMode) {
          this.aplicarDefaultsCriacao();
        }
      },
      error: () => {
        this.alertService.show(
          'Erro',
          'Não foi possível carregar os dados de configuração.',
          AlertType.error
        );
      },
    });
  }

  private aplicarDefaultsCriacao(): void {
    // Modo criação: Automático, Ação padrão, Unidade %
    const metodoAutomatico = this.listaMetodosAjuste.find(m => m.id === MetodoAjustePrecoEnum.Automatico);
    if (metodoAutomatico) {
      this.form.patchValue({ idMetodoAjustePreco: metodoAutomatico.id });
    }
    const acaoSomar = this.listaAcoes.find(a => a.descricao?.toLowerCase().includes('somar'));
    if (acaoSomar) {
      this.form.patchValue({ idAcaoAjustePreco: acaoSomar.id });
    }
    const unidadePercentual = this.listaUnidades.find(u => u.id === UnidadeAjustePrecoEnum.Percentual) || this.listaUnidades.find(u => u.descricao?.includes('%'));
    if (unidadePercentual) {
      this.form.patchValue({ idUnidadeAjustePreco: unidadePercentual.id });
    } else {
      this.form.patchValue({ idUnidadeAjustePreco: UnidadeAjustePrecoEnum.Percentual });
    }
  }

  private carregarDados(id: number): void {
    this.isLoading = true;
    this.listaPrecoService.get(id).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }

        const lista = res.listaPreco;
        this.isPadrao = lista.isPadrao;

        this.form.patchValue({
          descricao: lista.descricao,
          isAtivo: lista.isAtivo,
          isPromocao: lista.isPromocao,
          idMetodoAjustePreco: lista.idMetodoAjustePreco,
          idAcaoAjustePreco: lista.idAcaoAjustePreco,
          idUnidadeAjustePreco: lista.idUnidadeAjustePreco,
          valorAjuste: lista.valorAjuste,
          isSelecaoProdutosAutomatica: lista.isSelecaoProdutosAutomatica,
          isTodosClientes: lista.isTodosClientes,
          idTipoArredondamento: lista.idTipoArredondamento,
          dataInicioVigencia: lista.dataInicioVigencia ? new Date(lista.dataInicioVigencia) : null,
          dataTerminoVigencia: lista.dataTerminoVigencia ? new Date(lista.dataTerminoVigencia) : null,
          percentualDescontoSugerido: lista.percentualDescontoSugerido,
          percentualDescontoMaximo: lista.percentualDescontoMaximo,
        });

        if (res.produtos) {
          this.listaProdutos = [...res.produtos];
        }
        if (res.clientes) {
          this.listaClientes = [...res.clientes];
        }

        // Sincronizar máscara do campo Valor de Ajuste com a unidade carregada
        if (lista.idUnidadeAjustePreco) {
          this.onUnidadeAjusteChange(lista.idUnidadeAjustePreco);
        }

        // Em modo edição, bloquear campos imutáveis
        if (this.isEditMode) {
          this.form.get('idMetodoAjustePreco')?.disable();
          this.form.get('isSelecaoProdutosAutomatica')?.disable();
        }

        if (lista.idMetodoAjustePreco == MetodoAjustePrecoEnum.Manual) {
          this.form.get('idAcaoAjustePreco')?.disable();
          this.form.get('idUnidadeAjustePreco')?.disable();
          this.form.get('idUnidadeAjustePreco')?.clearValidators();
          this.form.get('idUnidadeAjustePreco')?.updateValueAndValidity();
          this.form.get('valorAjuste')?.disable();
          this.form.get('idTipoArredondamento')?.disable();
        }

        // Se padrão, desabilitar tudo
        if (this.isPadrao) {
          this.form.disable();
        }
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar a lista de preço.', AlertType.error);
      },
    });
  }

  private onPromocaoChange(): void {
    const isPromocao = this.form.get('isPromocao')?.value;
    const terminoCtrl = this.form.get('dataTerminoVigencia');
    if (isPromocao) {
      terminoCtrl?.setValidators([Validators.required]);
    } else {
      terminoCtrl?.clearValidators();
    }
    terminoCtrl?.updateValueAndValidity();
  }

  private onMetodoAjusteChange(idMetodo: number): void {
    const acaoCtrl = this.form.get('idAcaoAjustePreco');
    const selecaoCtrl = this.form.get('isSelecaoProdutosAutomatica');
    const valorAjusteCtrl = this.form.get('valorAjuste');
    const unidadeCtrl = this.form.get('idUnidadeAjustePreco');
    const arredondamentoCtrl = this.form.get('idTipoArredondamento');
    if (idMetodo !== MetodoAjustePrecoEnum.Automatico) {
      // Método Manual → força seleção manual de produtos, desabilita e LIMPA os campos de ajuste automático
      selecaoCtrl?.setValue(false, { emitEvent: false });
      selecaoCtrl?.disable();
      valorAjusteCtrl?.setValue(null);
      valorAjusteCtrl?.disable();
      valorAjusteCtrl?.clearValidators();
      arredondamentoCtrl?.disable();
      acaoCtrl?.setValue(null);
      acaoCtrl?.clearValidators();
      unidadeCtrl?.setValue(null);
      unidadeCtrl?.clearValidators();
    } else {
      // Método Automático → libera os campos e exige Ação, Unidade e Valor de Ajuste
      selecaoCtrl?.enable();
      valorAjusteCtrl?.enable();
      arredondamentoCtrl?.enable();
      acaoCtrl?.setValidators([Validators.required]);
      unidadeCtrl?.setValidators([Validators.required]);
      if (!unidadeCtrl?.value) {
        const unidadePercentual = this.listaUnidades.find(u => u.id === UnidadeAjustePrecoEnum.Percentual) || this.listaUnidades.find(u => u.descricao?.includes('%'));
        if (unidadePercentual) {
          unidadeCtrl?.setValue(unidadePercentual.id);
        } else {
          unidadeCtrl?.setValue(UnidadeAjustePrecoEnum.Percentual);
        }
      }
      const isPercent = unidadeCtrl?.value === UnidadeAjustePrecoEnum.Percentual;
      if (isPercent) {
        valorAjusteCtrl?.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
      } else {
        valorAjusteCtrl?.setValidators([Validators.required, Validators.min(0)]);
      }
    }
    acaoCtrl?.updateValueAndValidity();
    unidadeCtrl?.updateValueAndValidity();
    valorAjusteCtrl?.updateValueAndValidity();
  }

  // Getter para binding no template
  get isMetodoAutomatico(): boolean {
    return this.form.getRawValue().idMetodoAjustePreco === MetodoAjustePrecoEnum.Automatico;
  }

  get isPercentual(): boolean {
    return this.form.get('idUnidadeAjustePreco')?.value === UnidadeAjustePrecoEnum.Percentual;
  }

  get isSelecaoAutomatica(): boolean {
    return !!this.form.get('isSelecaoProdutosAutomatica')?.value;
  }

  onDateInput(event: Event, controlName: string): void {
    MaskService.onDateInput(event, this.form.get(controlName));
  }

  private onUnidadeAjusteChange(idUnidade: number): void {
    const isPercent = idUnidade === UnidadeAjustePrecoEnum.Percentual;
    this.valorAjusteMask = isPercent
      ? { ...this.maskService.getPercentMask(), allowNegative: false }
      : { ...this.maskService.getCurrencyMask(), allowNegative: false };

    const isAutomatico = this.form?.get('idMetodoAjustePreco')?.value === MetodoAjustePrecoEnum.Automatico;
    const valorCtrl = this.form?.get('valorAjuste');
    if (valorCtrl) {
      if (isAutomatico) {
        if (isPercent) {
          valorCtrl.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
        } else {
          valorCtrl.setValidators([Validators.required, Validators.min(0)]);
        }
      } else {
        valorCtrl.clearValidators();
      }
      valorCtrl.updateValueAndValidity();
    }
  }

  get isTodosClientes(): boolean {
    return !!this.form.get('isTodosClientes')?.value;
  }

  // Config de ajuste passado para o sub-componente de produtos
  get configAjuste() {
    const val = this.form.getRawValue();
    return {
      idAcaoAjustePreco: val.idAcaoAjustePreco,
      idUnidadeAjustePreco: val.idUnidadeAjustePreco,
      idTipoArredondamento: val.idTipoArredondamento,
      valorAjuste: val.valorAjuste,
    };
  }

  onProdutosChange(produtos: ProdutoListaPrecoStruct[]): void {
    this.listaProdutos = produtos;
  }

  onClientesChange(clientes: ClienteListaPrecoStruct[]): void {
    this.listaClientes = clientes;
  }

  salvar(): void {
    this.form.markAllAsTouched();

    if (!this.validar()) return;

    // Verificar se há edição inline pendente no sub-componente de produtos
    if (this.produtosComponent?.editingIndex !== null && this.produtosComponent?.editingIndex !== undefined) {
      const dialogRef = this.dialog.open(ConfirmModalComponent, {
        data: {
          title: 'Atenção',
          description:
            'Existe uma alteração de preço em andamento que ainda não foi confirmada e não será salva. Deseja continuar mesmo assim?',
          buttonConfirm: 'Continuar',
          buttonClose: 'Cancelar',
        },
      });

      dialogRef.afterClosed().subscribe({
        next: result => {
          if (result && result.confirm) {
            this.produtosComponent.cancelarEdicao();
            this.executarSalvar();
          }
        },
      });
      return;
    }

    this.executarSalvar();
  }

  private executarSalvar(): void {
    this.isSaving = true;

    const formVal = this.form.getRawValue();

    const request: ListaPrecoRequest = {
      descricao: formVal.descricao,
      isAtivo: formVal.isAtivo,
      isPromocao: formVal.isPromocao,
      idMetodoAjustePreco: formVal.idMetodoAjustePreco,
      idAcaoAjustePreco: formVal.idAcaoAjustePreco,
      idUnidadeAjustePreco: formVal.idUnidadeAjustePreco,
      valorAjuste: formVal.valorAjuste,
      isSelecaoProdutosAutomatica: formVal.isSelecaoProdutosAutomatica,
      isTodosClientes: formVal.isTodosClientes,
      idTipoArredondamento: formVal.idTipoArredondamento,
      dataInicioVigencia: formVal.dataInicioVigencia,
      dataTerminoVigencia: formVal.dataTerminoVigencia || undefined,
      percentualDescontoSugerido: formVal.percentualDescontoSugerido,
      percentualDescontoMaximo: formVal.percentualDescontoMaximo,
      produtos: this.listaProdutos.map(p => ({ idProduto: p.idProduto, precoLista: p.precoLista ?? null })),
      idsClientes: this.listaClientes.map(c => c.idCliente),
    };

    if (this.isEditMode && this.idListaPreco) {
      this.listaPrecoService.put(this.idListaPreco, request).subscribe({
        next: res => this.handleReturn(res),
        error: () => this.handleError(),
      });
    } else {
      this.listaPrecoService.post(request).subscribe({
        next: res => this.handleReturn(res),
        error: () => this.handleError(),
      });
    }
  }

  private validar(): boolean {
    if (this.isPadrao) return true;

    const formVal = this.form.getRawValue();

    if (!formVal.descricao) {
      this.alertService.show('Atenção', 'A descrição é obrigatória.', AlertType.warning);
      return false;
    }

    if (formVal.idMetodoAjustePreco === null || formVal.idMetodoAjustePreco === undefined) {
      this.alertService.show('Atenção', 'O Método de Ajuste é obrigatório.', AlertType.warning);
      return false;
    }

    if (formVal.isSelecaoProdutosAutomatica === null || formVal.isSelecaoProdutosAutomatica === undefined) {
      this.alertService.show('Atenção', 'O modo de seleção de produtos é obrigatório.', AlertType.warning);
      return false;
    }

    // Validações do modo automático
    if (this.isMetodoAutomatico) {
      if (!formVal.idAcaoAjustePreco) {
        this.alertService.show('Atenção', 'A Ação é obrigatória no modo automático.', AlertType.warning);
        return false;
      }
      if (!formVal.idUnidadeAjustePreco) {
        this.alertService.show('Atenção', 'A Unidade é obrigatória no modo automático.', AlertType.warning);
        return false;
      }
      // Valor de ajuste pode ser vazio apenas se houver desconto sugerido e máximo OU arredondamento
      const temDesconto = formVal.percentualDescontoSugerido || formVal.percentualDescontoMaximo;
      const temArredondamento = formVal.idTipoArredondamento;
      if (!formVal.valorAjuste && !temDesconto && !temArredondamento) {
        this.alertService.show(
          'Atenção',
          'O Valor de Ajuste é obrigatório quando não houver desconto ou arredondamento definidos.',
          AlertType.warning
        );
        return false;
      }
    }

    // Data de início
    if (!formVal.dataInicioVigencia) {
      this.alertService.show('Atenção', 'A Data de Início de Vigência é obrigatória.', AlertType.warning);
      return false;
    }
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const inicio = new Date(formVal.dataInicioVigencia);
    inicio.setHours(0, 0, 0, 0);
    if (inicio < hoje && !this.isEditMode) {
      this.alertService.show(
        'Atenção',
        'A Data de Início de Vigência deve ser maior ou igual à data atual.',
        AlertType.warning
      );
      return false;
    }

    // Data de término
    if (formVal.dataTerminoVigencia) {
      const termino = new Date(formVal.dataTerminoVigencia);
      termino.setHours(0, 0, 0, 0);
      if (termino < hoje) {
        this.alertService.show(
          'Atenção',
          'A Data de Término deve ser maior ou igual à data atual.',
          AlertType.warning
        );
        return false;
      }
      if (termino < inicio) {
        this.alertService.show(
          'Atenção',
          'A Data de Término não pode ser menor que a Data de Início.',
          AlertType.warning
        );
        return false;
      }
    }

    if (formVal.isPromocao && !formVal.dataTerminoVigencia) {
      this.alertService.show(
        'Atenção',
        'A Data de Término é obrigatória quando "É Promoção" está ativo.',
        AlertType.warning
      );
      return false;
    }

    // Validação de produtos
    if (!formVal.isSelecaoProdutosAutomatica) {
      if (this.listaProdutos.length === 0) {
        this.alertService.show('Atenção', 'Adicione ao menos um produto na lista.', AlertType.warning);
        return false;
      }
      if (!this.isMetodoAutomatico) {
        const semPreco = this.listaProdutos.some(p => !p.precoLista || p.precoLista <= 0);
        if (semPreco) {
          this.alertService.show(
            'Atenção',
            'Todos os produtos devem ter o Preço na Lista maior que zero no modo manual.',
            AlertType.warning
          );
          return false;
        }
      }
    }

    // Validação de clientes
    if (!formVal.isTodosClientes && this.listaClientes.length === 0) {
      this.alertService.show('Atenção', 'Adicione ao menos um cliente na lista.', AlertType.warning);
      return false;
    }

    return true;
  }

  private handleReturn(res: ReturnStruct): void {
    this.isSaving = false;
    if (res.isError) {
      this.alertService.show('Erro', res.errorDescription, AlertType.error);
    } else {
      this.alertService.show('Sucesso', 'Lista de preço salva com sucesso.', AlertType.success);
      this.router.navigate(['/configuracoes/cadastros/lista-precos']);
    }
  }

  private handleError(): void {
    this.isSaving = false;
    this.alertService.show('Erro', 'Ocorreu um erro ao salvar.', AlertType.error);
  }

  cancelar(): void {
    this.router.navigate(['/configuracoes/cadastros/lista-precos']);
  }
}
